import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Modal,
  useWindowDimensions,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import { TProcedure } from "../../../../utils/types";
import { adjustTextMap } from "../../../../utils/constant";
import {
  simulateTabBblImages,
  simulateTabBreastAugImages,
  simulateTabBreastLiftImages,
  simulateTabBreastLiftPlusAugImages,
  simulateTabBreastReduxImages,
  simulateTabFaceLiftImages,
  simulateTabHairTransplantImages,
  simulateTabLipFillerImages,
  simulateTabChinLipoImages,
  simulateTabTummyTuckImages,
  simulateTabAbdomenLipoImages,
  simulateTabMommyMakeoverImages,
  simulateTabRhinoplastyImages,
} from "./simulateTabConstants";
import ImageComparisonSlider from "../../../uncommon/web/image-comparison-slider/ImageComparisonSlider";
import UploadImage from "../../../uncommon/web/upload-image/UploadImage";
import ImageCarousel from "../../../uncommon/web/image-carousel/ImageCarousel";
import { useOnboardingTips } from "../../../../contexts/OnboardingTipsContext";
import DisplayOneSquare from "../../../uncommon/native/DisplayOneSquare";
import DisplayOneSquareCompare from "../../../uncommon/native/DisplayOneSquareCompare";
import DisplayTwoSquares from "../../../uncommon/native/DisplayTwoSquares";
import ConcatTwoSquares from "../../../uncommon/native/ConcatTwoSquares";
import { getPhotoUploadGuidance } from "./photoUploadGuidance";

interface TryAIComponentProps {
  isShowSlider: boolean;
  selectedProcedure: TProcedure;
  afterImageUrls: string[] | null;
  afterImageMetaData: any;
  saveCurrentImage: (image: Blob | string) => void;
  isMobile: boolean;
  suggestedImageUrls?: string[];
  handleSetIsAllowScrolling?: (isAllowScrolling: boolean) => void;
  refToModelSelector?: React.MutableRefObject<any>;
}

const TryAIComponent: React.FC<TryAIComponentProps> = ({
  isShowSlider,
  selectedProcedure,
  afterImageUrls,
  afterImageMetaData,
  saveCurrentImage,
  isMobile,
  suggestedImageUrls = [],
  handleSetIsAllowScrolling = () => {},
  refToModelSelector = { current: null },
}) => {
  const { tipsState } = useOnboardingTips();
  const { width } = useWindowDimensions();
  const [selectedView, setSelectedView] = useState<"Compare" | "Side-By-Side">(
    "Side-By-Side",
  );
  const [isViewerExpanded, setIsViewerExpanded] = useState(false);

  // Sizing for the expanded (fullscreen) viewer.
  const expandedWidth = Math.min(width * 0.9, 760);
  const expandedImageHalf = Math.min(Math.floor((expandedWidth - 40) / 2), 360);

  const hasSimulation =
    Array.isArray(afterImageUrls) && afterImageUrls.length > 0;

  const renderViewerBody = (expanded: boolean) => (
    <>
      {hasSimulation && (
        <View style={styles.toggleContainer}>
          <Pressable
            style={[
              styles.toggleButton,
              selectedView === "Side-By-Side" && styles.toggleButtonSelected,
            ]}
            onPress={() => setSelectedView("Side-By-Side")}
          >
            <Text style={styles.toggleText}>Side-By-Side</Text>
          </Pressable>
          <Pressable
            style={[
              styles.toggleButton,
              selectedView === "Compare" && styles.toggleButtonSelected,
            ]}
            onPress={() => setSelectedView("Compare")}
          >
            <Text style={styles.toggleText}>Compare</Text>
          </Pressable>
        </View>
      )}

      {selectedView === "Compare" ? (
        <ImageComparisonSlider
          adjustText={adjustTextMap[selectedProcedure]}
          imageUrls={afterImageUrls ?? []}
          metaData={afterImageMetaData}
          handleTurnOnOffScrollingStep3={handleSetIsAllowScrolling}
        />
      ) : (
        <ConcatTwoSquares
          currentImageData={[
            {
              uploadType: "TransformImage",
              presignedUrls: afterImageUrls ?? [],
              procedureType: [selectedProcedure],
            } as any,
          ]}
          assignableImageId={afterImageMetaData?.imageData?.id ?? null}
          imageSize={expanded ? expandedImageHalf : undefined}
        />
      )}
    </>
  );

  const procedureImageMap: Partial<Record<TProcedure, string[]>> = {
    BREAST_AUGMENTATION: simulateTabBreastAugImages,
    BREAST_LIFT: simulateTabBreastLiftImages,
    BREAST_REDUCTION: simulateTabBreastReduxImages,
    BREAST_LIFT_AND_AUGMENTATION: simulateTabBreastLiftPlusAugImages,
    BBL: simulateTabBblImages,
    TUMMY_TUCK: simulateTabTummyTuckImages,
    ABDOMEN_LIPOSUCTION: simulateTabAbdomenLipoImages,
    FACE_LIFT: simulateTabFaceLiftImages,
    LIP_FILLER: simulateTabLipFillerImages,
    RHINOPLASTY: simulateTabRhinoplastyImages,
    CHIN_LIPOSUCTION: simulateTabChinLipoImages,
    HAIR_TRANSPLANT: simulateTabHairTransplantImages,
    TUMMY_TUCK_AND_BREAST_LIFT: simulateTabMommyMakeoverImages,
    TUMMY_TUCK_AND_BREAST_REDUCTION: simulateTabMommyMakeoverImages,
    TUMMY_TUCK_AND_BREAST_AUGMENTATION: simulateTabMommyMakeoverImages,
  };

  const imageUrls =
    suggestedImageUrls.length > 0
      ? suggestedImageUrls
      : (procedureImageMap[selectedProcedure] ?? []);
  const photoUploadGuidance = getPhotoUploadGuidance(selectedProcedure);

  return (
    <View
      style={[
        styles.tryAiInnerContainer,
        { width: isMobile ? "100%" : 316, gap: 5 },
      ]}
    >
      {isShowSlider ? (
        <View style={styles.viewerWrap}>
          {hasSimulation && (
            <Pressable
              style={styles.expandButton}
              onPress={() => setIsViewerExpanded(true)}
              accessibilityLabel="Open simulation in fullscreen"
            >
              <Ionicons name="expand-outline" size={18} color="#333" />
            </Pressable>
          )}
          {renderViewerBody(false)}
        </View>
      ) : (
        <UploadImage
          guidance={photoUploadGuidance}
          saveCurrentImage={saveCurrentImage}
        />
      )}

      <Modal
        visible={isViewerExpanded}
        transparent
        animationType="fade"
        onRequestClose={() => setIsViewerExpanded(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable
            style={styles.modalBackdrop}
            onPress={() => setIsViewerExpanded(false)}
          />
          <View style={[styles.modalContent, { width: expandedWidth }]}>
            <Pressable
              style={styles.modalClose}
              onPress={() => setIsViewerExpanded(false)}
              accessibilityLabel="Close fullscreen viewer"
            >
              <Ionicons name="close" size={24} color="#333" />
            </Pressable>
            {renderViewerBody(true)}
          </View>
        </View>
      </Modal>

      <ImageCarousel
        imageUrls={imageUrls}
        saveCurrentImage={saveCurrentImage}
        onRefReady={(ref) => {
          refToModelSelector.current = ref;
        }}
        highlightedIndex={
          tipsState.selectModel && !tipsState.selectSecondProcedure
            ? 0
            : undefined
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  tryAiInnerContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginHorizontal: "auto",
  },
  modelText: {
    fontFamily: "Urbanist500",
    fontWeight: "500",
    color: "#8F9195",
    marginVertical: 12,
  },
  viewerWrap: {
    width: "100%",
    position: "relative",
  },
  expandButton: {
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: 5,
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "rgba(255,255,255,0.9)",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  },
  modalOverlay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.7)",
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 24,
    alignItems: "center",
    maxWidth: "90%",
  },
  modalClose: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 5,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
    justifyContent: "center",
  },
  toggleContainer: {
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 12,
  },
  toggleButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: "#F0F0F0",
    marginHorizontal: 4,
  },
  toggleButtonSelected: {
    backgroundColor: "#00B7F41A",
  },
  toggleText: {
    fontFamily: "Urbanist600",
    fontSize: 14,
  },
});

export default TryAIComponent;
